/* Midnight · Home chapter — films, reveal curtains, and the verification
   steps that follow the inspection film. Vanilla, idempotent, theme-editor
   aware. */
(() => {
  if (window.__mwHomeChapter) return;
  window.__mwHomeChapter = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasIO = typeof IntersectionObserver === 'function';
  const cleanups = new WeakMap();

  const format = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m + ':' + String(s).padStart(2, '0');
  };

  /* ---- Films ----------------------------------------------------------------
     Click to play with sound. Native controls are the no-JS fallback and are
     removed here. Metadata is only fetched once the frame is near the
     viewport; playback pauses if the frame leaves it. */
  const initVideo = (root) => {
    if (root.dataset.mwcVideoInit) return;
    const video = root.querySelector('video');
    const toggle = root.querySelector('[data-mwc-toggle]');
    if (!video || !toggle) return;
    root.dataset.mwcVideoInit = '1';

    const label = root.querySelector('[data-mwc-label]');
    const progress = root.querySelector('[data-mwc-progress]');
    const steps = video.id ? document.querySelector('[data-mwc-steps][data-mwc-for="' + video.id + '"]') : null;
    const stepButtons = steps ? [...steps.querySelectorAll('[data-mwc-step]')] : [];
    const idle = toggle.dataset.mwcIdle || 'Play';
    const controller = new AbortController();
    const { signal } = controller;
    let duration = 0;
    let observer = null;

    video.removeAttribute('controls');

    const render = () => {
      const playing = !video.paused && !video.ended;
      const midway = video.currentTime > 0 && !video.ended;
      toggle.classList.toggle('is-playing', playing);
      toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      if (progress) {
        const ratio = duration ? Math.min(1, video.currentTime / duration) : 0;
        progress.style.transform = 'scaleX(' + ratio.toFixed(4) + ')';
      }
      if (stepButtons.length) {
        let active = 0;
        if (video.currentTime > 0 && !video.ended) {
          stepButtons.forEach((button, index) => {
            if (video.currentTime >= parseFloat(button.dataset.mwcTime || '0')) active = index;
          });
        }
        stepButtons.forEach((button, index) => {
          const on = index === active;
          button.classList.toggle('is-active', on);
          button.setAttribute('aria-current', on ? 'step' : 'false');
        });
      }
      if (!label) return;
      if (playing || midway) {
        label.textContent = format(video.currentTime) + ' / ' + format(duration);
      } else {
        label.textContent = duration ? idle + ' \u00b7 ' + format(duration) : idle;
      }
    };

    const warm = () => {
      if (video.preload === 'none') {
        video.preload = 'metadata';
        video.load();
      }
    };

    toggle.addEventListener(
      'click',
      () => {
        if (video.paused || video.ended) {
          if (video.ended) video.currentTime = 0;
          video.muted = false;
          const request = video.play();
          if (request && typeof request.catch === 'function') request.catch(render);
        } else {
          video.pause();
        }
      },
      { signal }
    );

    stepButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const time = parseFloat(button.dataset.mwcTime || '0');
          const go = () => {
            video.currentTime = time;
            video.muted = false;
            const request = video.play();
            if (request && typeof request.catch === 'function') request.catch(render);
          };
          const rect = root.getBoundingClientRect();
          const visible = rect.top >= 0 && rect.bottom <= window.innerHeight;
          if (!visible) root.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' });
          if (video.readyState >= 1) {
            go();
          } else {
            warm();
            video.addEventListener('loadedmetadata', go, { once: true, signal });
          }
        },
        { signal }
      );
    });

    video.addEventListener('loadedmetadata', () => { duration = video.duration; render(); }, { signal });
    video.addEventListener('durationchange', () => { duration = video.duration; render(); }, { signal });
    ['play', 'pause', 'ended', 'timeupdate'].forEach((name) => video.addEventListener(name, render, { signal }));

    if (hasIO) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              warm();
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { rootMargin: '40% 0px 40% 0px', threshold: 0 }
      );
      observer.observe(root);
    } else {
      warm();
    }

    render();

    cleanups.set(root, () => {
      controller.abort();
      if (observer) observer.disconnect();
      delete root.dataset.mwcVideoInit;
    });
  };

  /* ---- Reveal curtains ---------------------------------------------------- */
  const initCurtain = (frame) => {
    if (frame.dataset.mwcCurtainInit) return;
    frame.dataset.mwcCurtainInit = '1';

    if (reduced.matches || !hasIO) {
      frame.classList.add('is-in');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          frame.classList.add('is-in');
          observer.disconnect();
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );
    observer.observe(frame);
    cleanups.set(frame, () => observer.disconnect());
  };

  /* ---- Wiring ---------------------------------------------------------------- */
  const initWithin = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-mwc-video]').forEach(initVideo);
    scope.querySelectorAll('[data-mwc-curtain]').forEach(initCurtain);
  };

  const teardownWithin = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-mwc-video], [data-mwc-curtain]').forEach((el) => {
      const cleanup = cleanups.get(el);
      if (cleanup) {
        cleanup();
        cleanups.delete(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document));
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardownWithin(event.target));
})();
